import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import RichTextEditor from './RichTextEditor';
import { useTemplatesModel } from '../hooks/useTemplatesModel';

interface AddTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  /** When provided, edit this existing template instead of creating a new one. */
  editTemplateId?: string | null;
}

/**
 * Create or edit a note template: a name field + a lite rich editor for the
 * template body. (There is no "save current note as template" flow — templates
 * are authored here directly.)
 */
const AddTemplateDialog: React.FC<AddTemplateDialogProps> = ({
  open,
  onClose,
  editTemplateId,
}) => {
  const { templates, addTemplate, updateTemplate, saving } =
    useTemplatesModel();
  const editing = editTemplateId
    ? templates.find((t) => t.template_id === editTemplateId)
    : undefined;

  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [error, setError] = useState('');

  // Seed fields when opening (fresh for create, existing values for edit).
  useEffect(() => {
    if (open) {
      setName(editing?.template_name ?? '');
      setContent(editing?.template_content ?? '');
      setError('');
    }
  }, [open, editing]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Template name is required.');
      return;
    }
    const result = editTemplateId
      ? await updateTemplate(editTemplateId, {
          template_name: trimmed,
          template_content: content,
        })
      : await addTemplate(trimmed, content);

    if (result.success) {
      onClose();
    } else {
      setError(result.error || 'Failed to save template.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editTemplateId ? 'Edit Template' : 'Add Template'}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Template Name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 100 }}
          error={!!error}
          helperText={error}
          disabled={saving}
        />
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Template content
          </Typography>
          <RichTextEditor
            key={editTemplateId ?? 'new'}
            initialContent={content}
            onChange={setContent}
            preset="lite"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTemplateDialog;
